import type {
  ActeurParcoursHabilitation,
  CodeEtapeParcoursHabilitation,
} from './habilitation-parcours.model';
import type { HabilitationStatut } from './types/habilitation-statut.type';
import {
  getHabilitationStatusPresentation,
  type HabilitationStatusPresentation,
} from './habilitation-status-presentation';

export interface HabilitationCurrentStepPresentation
  extends HabilitationStatusPresentation {
  label: string;
}

type CurrentStepLabelPresentationByCode = Partial<
  Record<CodeEtapeParcoursHabilitation, string>
>;

const DEFAULT_CURRENT_STEP_LABELS: Record<ActeurParcoursHabilitation, string> = {
  consultant: 'En attente de traitement par nos services',
  shm: 'En attente du consultant',
};

const CURRENT_STEP_LABELS: Record<
  ActeurParcoursHabilitation,
  CurrentStepLabelPresentationByCode
> = {
  consultant: {
    pieces: 'Vérification en cours',
    collaboration_stefi: 'Collaboration à finaliser',
    demande_orias: 'En attente de vérification',
    finalisation_orias: 'Finaliser inscription ORIAS',
    controle_pieces_shm: 'Vérification en cours',
    declaration_mandat_orias_shm: 'Déclaration ORIAS en cours',
    retour_orias_shm: 'Traitement en cours consultant / ORIAS',
    formation_immo: 'Formation à effectuer',
    formation_habilitante_alpsi: 'Formation habilitante à effectuer',
    formation_habilitante_assurance: 'Formation habilitante à effectuer',
    formation_habilitante_financement: 'Formation habilitante à effectuer',
    formation_habilitante_cif: 'Formation habilitante à effectuer',
    cotisation_cci: 'Cotisation CCI à régler',
    adhesion_association_cif: "Adhésion à l'association CIF en cours",
    demande_suppression_mandat_alpsi_cif: 'Demande de suppression en cours',
    inscription_orias_cif: 'Déclaration CIF en cours',
    retour_orias_cif: 'Traitement en cours consultant / ORIAS',
    convention_distribution_cif: 'Convention CIF à transmettre',
    envoi_signature_convention_cif_shm: 'Convention CIF en signature',
    signature_convention_cif: 'Signature de la convention CIF en cours',
    envoi_signature_mandat_alpsi_shm: 'Mandat ALPSI en signature',
    signature_mandat_alpsi: 'Mandat ALPSI à signer',
    declaration_mandant_orias_alpsi_shm: 'Déclaration ORIAS en cours',
    finalisation_dossier_orias_alpsi: 'Finalisation du dossier ORIAS',
    generation_carte_demarchage_alpsi_shm: 'Génération de la carte en cours',
    generation_carte_demarchage_financement_shm: 'Génération de la carte en cours',
    retour_cci_shm: 'Dossier en cours de traitement par la CCI de Toulouse',
  },
  shm: {
    controle_pieces_shm: 'À contrôler',
    declaration_mandat_orias_shm: 'Déclaration ORIAS à effectuer',
    retour_orias_shm: 'Traitement en cours consultant / ORIAS',
    controle_formation_immo_shm: 'Contrôle de la formation à effectuer',
    generation_cerfa_cci_shm: 'Génération du Cerfa CCI à effectuer',
    constitution_dossier_cci_shm: 'Constitution du dossier CCI à effectuer',
    retour_cci_shm: 'Attente retour CCI',
    attestation_collaborateur_shm: 'Attestation collaborateur à effectuer',
    activation_habilitation_immo_shm: "Activation de l'habilitation à effectuer",
    suppression_mandat_alpsi_orias_shm: 'Suppression du mandat en cours',
    confirmation_suppression_mandat_alpsi_shm:
      'Confirmation de la suppression à effectuer',
    retour_orias_cif: 'Retour ORIAS CIF à traiter',
    envoi_signature_convention_cif_shm: 'Convention CIF à envoyer en signature',
    signature_convention_cif: 'Signature de la convention CIF en cours',
    envoi_signature_mandat_alpsi_shm: 'Mandat ALPSI à envoyer en signature',
    declaration_mandant_orias_alpsi_shm: 'Déclaration ORIAS à effectuer',
    generation_carte_demarchage_alpsi_shm: 'Génération de la carte à effectuer',
    generation_carte_demarchage_financement_shm: 'Génération de la carte à effectuer',
  },
};

const CURRENT_STEP_LABEL_OVERRIDES: Record<
  ActeurParcoursHabilitation,
  Partial<Record<HabilitationStatut, string>>
> = {
  consultant: {
    pret: 'À compléter',
    nc_a_traiter: 'À corriger',
    nc_traitee: 'Corrigé',
  },
  shm: {
    nc_a_traiter: 'NC à traiter',
    nc_traitee: 'NC traitée',
  },
};

export function getHabilitationCurrentStepLabel(
  actor: ActeurParcoursHabilitation,
  code?: CodeEtapeParcoursHabilitation,
  statut?: HabilitationStatut | string
): string {
  return (
    (statut
      ? CURRENT_STEP_LABEL_OVERRIDES[actor][statut as HabilitationStatut]
      : undefined) ??
    (code ? CURRENT_STEP_LABELS[actor][code] : undefined) ??
    DEFAULT_CURRENT_STEP_LABELS[actor]
  );
}

export function getHabilitationCurrentStepPresentation(
  actor: ActeurParcoursHabilitation,
  code?: CodeEtapeParcoursHabilitation,
  statut?: HabilitationStatut | string
): HabilitationCurrentStepPresentation {
  const statutForPresentation =
    actor === 'shm' &&
    statut === 'action_consultant' &&
    code !== undefined &&
    CURRENT_STEP_LABELS.shm[code] !== undefined
      ? 'action_backoffice'
      : statut;

  return {
    label: getHabilitationCurrentStepLabel(actor, code, statutForPresentation),
    ...getHabilitationStatusPresentation(actor, statutForPresentation),
  };
}
